import { TrashIcon } from '@heroicons/react/20/solid'
import { PlusIcon } from '@heroicons/react/24/outline'
import Editor from '@monaco-editor/react'
import { getDefaultRegistry, withTheme } from '@rjsf/core'
import {
  ADDITIONAL_PROPERTY_FLAG,
  ArrayFieldTemplateItemType,
  ArrayFieldTemplateProps,
  BaseInputTemplateProps,
  canExpand,
  descriptionId,
  FieldProps,
  FieldTemplateProps,
  FormContextType,
  getSubmitButtonOptions,
  getTemplate,
  getUiOptions,
  getWidget,
  IconButtonProps,
  ObjectFieldTemplatePropertyType,
  ObjectFieldTemplateProps,
  RJSFSchema,
  StrictRJSFSchema,
  SubmitButtonProps,
  TitleFieldProps,
  titleId,
  UiSchema,
  WidgetProps,
  WrapIfAdditionalTemplateProps
} from '@rjsf/utils'
import { isObject, merge, startCase } from 'lodash'

import CopyTextButton from '@/components/CopyTextButton'
import Checkbox from '@/components/TailwindUI/Checkbox'
import cn from '@/utilities/cn'

export default withTheme<any>({
  templates: {
    FieldTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: FieldTemplateProps<T, S, F>
    ) => {
      const { id, label, children, errors, help, description, hidden, required, displayLabel, registry, uiSchema } =
        props
      const uiOptions = getUiOptions(uiSchema)
      const WrapIfAdditionalTemplate = getTemplate<'WrapIfAdditionalTemplate', T, S, F>(
        'WrapIfAdditionalTemplate',
        registry,
        uiOptions
      )
      if (hidden) {
        return <div className="hidden">{children}</div>
      }
      return (
        <WrapIfAdditionalTemplate
          {...props}
          classNames={cn(props.classNames, { 'group-[.install-form]:overflow-auto': props.id === 'root' })}
        >
          {displayLabel && (
            <label
              htmlFor={id}
              className="group-[.builder-form]:neutral-600 block text-sm font-medium leading-6 text-gray-900 group-[.builder-form]:font-bold"
            >
              {startCase(label)}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
          )}
          {displayLabel && description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
          <div className="mt-2">{children}</div>
          <p className="mt-1 text-sm text-red-500">{errors}</p>
          <p className="mt-2 text-sm text-gray-500">{help}</p>
        </WrapIfAdditionalTemplate>
      )
    },
    BaseInputTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: BaseInputTemplateProps<T, S, F>
    ) => {
      const { registry, uiSchema } = props
      const uiOptions = getUiOptions(uiSchema)
      const BaseInputTemplate = getTemplate<'BaseInputTemplate', T, S, F>(
        'BaseInputTemplate',
        { ...registry, ...getDefaultRegistry() },
        uiOptions
      )

      return (
        <BaseInputTemplate
          {...props}
          className={cn(
            props.classNames,
            'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
            { 'bg-gray-100 text-gray-600': props.disabled }
          )}
        />
      )
    },
    WrapIfAdditionalTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: WrapIfAdditionalTemplateProps<T, S, F>
    ) => {
      const { id, classNames, style, disabled, label, onKeyChange, onDropPropertyClick, readonly, schema, children } =
        props
      // Button templates are not overridden in the uiSchema
      const additional = ADDITIONAL_PROPERTY_FLAG in schema

      if (!additional) {
        return (
          <div className={classNames} style={style}>
            {children}
          </div>
        )
      }

      return (
        <div className={classNames} style={style}>
          <div className="row">
            <div className="col-xs-5 form-additional">
              <div className="form-group">
                <input
                  className="mr-2 mt-2 block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 group-[.builder-form]:mt-[12px] group-[.builder-form]:h-[48px] sm:text-sm sm:leading-6"
                  type="text"
                  id={`${id}-key`}
                  onBlur={event => onKeyChange(event.target.value)}
                  defaultValue={label}
                />
              </div>
            </div>
            <div className="form-additional form-group col-xs-5">{children}</div>
            <div className="col-xs-2">
              <button
                type="button"
                disabled={disabled || readonly}
                onClick={onDropPropertyClick(label)}
                className="bg-grey-light hover:bg-grey text-grey-darkest mt-3 inline-flex items-center rounded py-1 px-1 font-bold"
              >
                <div className="flex h-[20px] w-[20px] group-[.builder-form]:mt-2">
                  <TrashIcon className="text-gray-600" />
                </div>
              </button>
            </div>
          </div>
        </div>
      )
    },
    TitleFieldTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: TitleFieldProps<T, S, F>
    ) => {
      const { id, title, required } = props
      return (
        <legend id={id} className="mb-2 text-xs uppercase text-gray-400">
          {title}
          {required && <span>*</span>}
        </legend>
      )
    },
    ArrayFieldTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: ArrayFieldTemplateProps<T, S, F>
    ) => {
      const {
        canAdd,
        className,
        disabled,
        idSchema,
        uiSchema,
        items,
        onAddClick,
        readonly,
        registry,
        required,
        schema,
        title
      } = props
      const uiOptions = getUiOptions<T, S, F>(uiSchema)
      const ArrayFieldDescriptionTemplate = getTemplate<'ArrayFieldDescriptionTemplate', T, S, F>(
        'ArrayFieldDescriptionTemplate',
        registry,
        uiOptions
      )
      const ArrayFieldItemTemplate = getTemplate<'ArrayFieldItemTemplate', T, S, F>(
        'ArrayFieldItemTemplate',
        registry,
        uiOptions
      )
      const ArrayFieldTitleTemplate = getTemplate<'ArrayFieldTitleTemplate', T, S, F>(
        'ArrayFieldTitleTemplate',
        registry,
        uiOptions
      )
      // Button templates are not overridden in the uiSchema
      const {
        ButtonTemplates: { AddButton }
      } = registry.templates
      return (
        <fieldset className={className} id={idSchema.$id}>
          <ArrayFieldTitleTemplate
            idSchema={idSchema}
            title={uiOptions.title || title}
            required={required}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
          <ArrayFieldDescriptionTemplate
            idSchema={idSchema}
            description={uiOptions.description || schema.description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
          <div className="row array-item-list flex flex-col gap-2">
            {items &&
              items.map(({ key, ...itemProps }: ArrayFieldTemplateItemType<T, S, F>) => (
                <ArrayFieldItemTemplate key={key} {...itemProps} />
              ))}
          </div>
          {canAdd && (
            <AddButton
              className="array-item-add"
              onClick={onAddClick}
              disabled={disabled || readonly}
              uiSchema={uiSchema}
              registry={registry}
            />
          )}
        </fieldset>
      )
    },
    ObjectFieldTemplate: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: ObjectFieldTemplateProps<T, S, F>
    ) => {
      const {
        description,
        disabled,
        formData,
        idSchema,
        onAddClick,
        properties,
        readonly,
        registry,
        required,
        schema,
        title,
        uiSchema
      } = props
      const options = getUiOptions<T, S, F>(uiSchema)
      const TitleFieldTemplate = getTemplate<'TitleFieldTemplate', T, S, F>('TitleFieldTemplate', registry, options)
      const DescriptionFieldTemplate = getTemplate<'DescriptionFieldTemplate', T, S, F>(
        'DescriptionFieldTemplate',
        registry,
        options
      )
      // Button templates are not overridden in the uiSchema
      const {
        ButtonTemplates: { AddButton }
      } = registry.templates
      return (
        <fieldset id={idSchema.$id}>
          {title && (
            <TitleFieldTemplate
              id={titleId<T>(idSchema)}
              title={title}
              required={required}
              schema={schema}
              uiSchema={uiSchema}
              registry={registry}
            />
          )}
          {description && (
            <DescriptionFieldTemplate
              id={descriptionId<T>(idSchema)}
              description={description}
              schema={schema}
              uiSchema={uiSchema}
              registry={registry}
            />
          )}
          {properties.map((prop: ObjectFieldTemplatePropertyType) => prop.content)}
          {canExpand<T, S, F>(schema, uiSchema, formData) && (
            <AddButton
              className="object-property-expand"
              onClick={onAddClick(schema)}
              disabled={disabled || readonly}
              uiSchema={uiSchema}
              registry={registry}
            />
          )}
        </fieldset>
      )
    },
    ButtonTemplates: {
      AddButton: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>({
        className,
        onClick,
        disabled
      }: IconButtonProps<T, S, F>) => {
        return (
          <div className="row">
            <p className={`col-xs-3 col-xs-offset-9 text-right ${className}`}>
              <button
                type="button"
                className={cn(
                  'flex w-full cursor-pointer justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                )}
                onClick={onClick}
                disabled={disabled}
              >
                <div className="flex h-[20px] w-[20px]">
                  <PlusIcon className="text-white" />
                </div>
              </button>
            </p>
          </div>
        )
      },
      SubmitButton: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>({
        uiSchema
      }: SubmitButtonProps<T, S, F>) => {
        const { submitText, norender, props: submitButtonProps = {} } = getSubmitButtonOptions<T, S, F>(uiSchema)
        if (norender) {
          return null
        }
        return (
          <div className="flex justify-end">
            <button
              type="submit"
              {...submitButtonProps}
              className={cn(
                submitButtonProps.className,
                'flex rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
                { 'pointer-events-none opacity-60': submitButtonProps.disabled }
              )}
            >
              {submitText}
            </button>
          </div>
        )
      }
    }
  },
  widgets: {
    SelectWidget: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: WidgetProps<T, S, F>
    ) => {
      const SelectWidget = getWidget<T, S, F>(props.schema, 'SelectWidget', getDefaultRegistry().widgets as any)

      return (
        <SelectWidget
          {...props}
          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        />
      )
    },
    TextareaWidget: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: WidgetProps<T, S, F>
    ) => {
      const TextareaWidget = getWidget<T, S, F>(props.schema, 'TextareaWidget', getDefaultRegistry().widgets as any)

      return (
        <TextareaWidget
          {...props}
          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        />
      )
    },
    CheckboxWidget: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: WidgetProps<T, S, F>
    ) => {
      return (
        <Checkbox
          className="mt-2"
          onChange={() => props.onChange(!props.value)}
          checked={props.value}
          disabled={props.disabled}
          label={props.label}
        />
      )
    },
    CallBackUrl: (props: WidgetProps) => {
      return (
        <>
          <span className="text-sm text-gray-400">{props.value}</span>
          <CopyTextButton text={props.value} />
        </>
      )
    },
    FileUpload: <T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
      props: WidgetProps<T, S, F>
    ) => {
      const FileWidget = getWidget<T, S, F>(props.schema, 'file', getDefaultRegistry().widgets as any)

      return <FileWidget {...props} />
    }
  },
  fields: {
    ObjectField: function (props: FieldProps) {
      const isJson = (value: string) => {
        try {
          if (isObject(value)) {
            return true
          }
          JSON.parse(value)
          return true
        } catch {
          return false
        }
      }
      const { id, required } = props

      const ObjectField = getDefaultRegistry().fields.ObjectField

      if (props.schema.properties) {
        return <ObjectField {...props} />
      } else {
        return (
          <>
            {props.schema.title && (
              <label htmlFor={id} className="block text-sm font-medium leading-6 text-gray-900">
                {props.schema.title}
                {required && <span>*</span>}
              </label>
            )}
            <div className="relative block h-[120px] w-full rounded-md border-0 p-1 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6">
              <Editor
                options={{
                  lineNumbers: 'off',
                  glyphMargin: false,
                  folding: false,
                  minimap: { enabled: false },
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 0
                }}
                onMount={() => {
                  if (!props.formData) {
                    props.onChange(props.schema.default)
                  }
                }}
                language="json"
                value={isObject(props.formData) ? JSON.stringify(props.formData, null, 2) : props.formData}
                onChange={(value = '') => {
                  isJson(value) ? props.onChange(JSON.parse(value)) : props.onChange(value)
                }}
                defaultValue={isJson(JSON.stringify(props.formData)) ? JSON.stringify(props.formData) : props.formData}
              />
            </div>
          </>
        )
      }
    }
  }
})

/**
 * Given a jsonschema, extract the `x-ui:` properties into a uiSchema
 *
 * @param schema
 * @returns
 */
export function extractUiSchema(schema: RJSFSchema): UiSchema | undefined {
  const schemas: UiSchema[] = []

  function collect(schema: RJSFSchema, fields?: string | string[]) {
    if (!schema) return
    if (!fields) fields = Object.keys(schema)
    if (!Array.isArray(fields)) fields = [fields]

    for (const field of fields) {
      if (!schema[field]) continue

      // default to hide label on additional props value as causes the query params to be misaligned
      if (field === 'params') {
        schemas.push({ [field]: { additionalProperties: { 'ui:label': false } } })
      }

      for (const [key, value] of Object.entries(schema[field])) {
        if (key.startsWith('x-ui:')) {
          schemas.push({ [field]: { [key.slice(2)]: value } })
        }
      }

      const nestedSchema = extractUiSchema(schema[field] as RJSFSchema)
      if (nestedSchema) schemas.push({ [field]: nestedSchema })
    }
  }

  // Objects
  collect(schema?.properties ?? {}) // Properties are hoisted...
  collect(schema, 'additionalProperties')

  // Arrays
  collect(schema, 'items')
  collect(schema, 'additionalItems')

  // Dependencies
  for (const dependency of Object.values(schema?.dependencies ?? {})) {
    if (typeof dependency !== 'object' || !('oneOf' in dependency) || !Array.isArray(dependency.oneOf)) continue

    for (const definition of dependency.oneOf) {
      const nestedSchema = extractUiSchema(definition as RJSFSchema)
      if (nestedSchema) schemas.push(nestedSchema)
    }
  }

  if (schemas.length === 0) return
  return merge({}, ...schemas)
}
